import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Check, Copy, FileText, MessageSquare, ChevronRight } from 'lucide-react';
import { Container } from '../components/ui/Container/Container';
import { Badge } from '../components/ui/Badge/Badge';
import { PublicHeader } from '../components/Layout/PublicHeader';
import { PublicFooter } from '../components/Layout/PublicFooter';
import { useAuthStore } from '../stores/authStore';
import { FeedbackModal } from '../components/Feedback/FeedbackModal';

const SUPPORT_EMAIL = 'rohit@zeropointstudio.io';

function CopyEmailButton() {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = SUPPORT_EMAIL;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  return (
    <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 border">
      <Mail className="w-5 h-5 text-gray-400 shrink-0" />
      <a href={`mailto:${SUPPORT_EMAIL}`} className="text-foreground font-medium hover:text-primary-600 transition-colors flex-1 truncate">
        {SUPPORT_EMAIL}
      </a>
      <button
        onClick={handleCopy}
        className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-foreground"
      >
        {copied ? (
          <>
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-green-600">Copied</span>
          </>
        ) : (
          <>
            <Copy className="w-4 h-4" />
            Copy
          </>
        )}
      </button>
    </div>
  );
}

export function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const user = useAuthStore(s => s.user);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackTab, setFeedbackTab] = useState<'bug' | 'feature' | 'help'>('help');

  const openFeedback = (tab: 'bug' | 'feature' | 'help') => {
    setFeedbackTab(tab);
    setFeedbackOpen(true);
  };

  const contactMethods = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Support',
      description: 'Reach out directly for any questions or issues',
      contact: SUPPORT_EMAIL,
      responseTime: 'Within 24 hours'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Help Center',
      description: 'Browse articles and guides for common questions',
      contact: 'Self-service documentation',
      responseTime: 'Instant',
      href: '/help'
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'In-App Support',
      description: 'Report bugs, request features, or chat with AI help',
      contact: 'Available when logged in',
      responseTime: 'Instant AI + team follow-up',
      requiresAuth: true
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      {/* Hero Section */}
      <section className="bg-white py-16">
        <Container size="lg">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Have a question, need help, or want to report an issue?
              We're here to help.
            </p>
          </div>
        </Container>
      </section>

      {/* Contact Methods */}
      <section className="py-16">
        <Container size="lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {contactMethods.map((method, index) => {
              const isClickable = method.href || (method.requiresAuth && user);

              const card = (
                <div className={`bg-white rounded-xl p-6 text-center shadow-sm border hover:shadow-md transition-shadow h-full ${isClickable ? 'cursor-pointer' : ''}`}>
                  <div className="w-12 h-12 bg-primary-50 text-primary-700 rounded-lg grid place-items-center mx-auto mb-4">
                    {method.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{method.title}</h3>
                  <p className="text-muted-foreground mb-3">{method.description}</p>
                  <p className="font-medium text-foreground mb-1 text-sm">{method.contact}</p>
                  <Badge variant="secondary" className="text-xs">
                    {method.responseTime}
                  </Badge>
                </div>
              );

              if (method.href) {
                return <Link key={index} to={method.href}>{card}</Link>;
              }
              if (method.requiresAuth && user) {
                return <button key={index} onClick={() => openFeedback('help')} className="text-left w-full">{card}</button>;
              }
              return <div key={index}>{card}</div>;
            })}
          </div>
        </Container>
      </section>

      {/* Email + Support Section */}
      <section className="py-16 bg-white">
        <Container size="lg">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Email Contact */}
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">Email Us Directly</h2>
              <p className="text-muted-foreground mb-6">
                For general questions, partnership inquiries, billing issues, or anything else — send us an email and
                we'll get back to you within 24 hours.
              </p>

              <CopyEmailButton />

              <div className="mt-8 space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Common Inquiries</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <span className="text-lg shrink-0">💳</span>
                    <div>
                      <p className="font-medium text-foreground">Billing &amp; Payments</p>
                      <p className="text-sm text-muted-foreground">Subscription questions, invoice requests, refund inquiries</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg shrink-0">🤝</span>
                    <div>
                      <p className="font-medium text-foreground">Partnerships &amp; Enterprise</p>
                      <p className="text-sm text-muted-foreground">Custom plans, API access, integration partnerships</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-lg shrink-0">📋</span>
                    <div>
                      <p className="font-medium text-foreground">General Questions</p>
                      <p className="text-sm text-muted-foreground">Product info, feature availability, account questions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* In-App Support */}
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-6">In-App Support</h2>
              <p className="text-muted-foreground mb-6">
                Logged-in users can access our built-in support system directly from the app.
                Report bugs, request features, or get instant help from our AI assistant.
              </p>

              <div className="space-y-4">
                {/* Bug Report */}
                <div
                  className={`border rounded-lg p-5 transition-all ${user ? 'hover:shadow-md cursor-pointer hover:border-primary-200' : 'opacity-70'}`}
                  onClick={user ? () => openFeedback('bug') : undefined}
                  role={user ? 'button' : undefined}
                  tabIndex={user ? 0 : undefined}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-2xl shrink-0">🐛</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground mb-1">Report a Bug</h3>
                      <p className="text-sm text-muted-foreground">
                        Found something broken? Report it with screenshots and we'll investigate.
                      </p>
                    </div>
                    {user ? (
                      <ChevronRight className="w-5 h-5 text-gray-400 mt-1 shrink-0" />
                    ) : (
                      <Badge variant="secondary" className="text-xs mt-1 shrink-0">Login required</Badge>
                    )}
                  </div>
                </div>

                {/* Feature Request */}
                <div
                  className={`border rounded-lg p-5 transition-all ${user ? 'hover:shadow-md cursor-pointer hover:border-primary-200' : 'opacity-70'}`}
                  onClick={user ? () => openFeedback('feature') : undefined}
                  role={user ? 'button' : undefined}
                  tabIndex={user ? 0 : undefined}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-2xl shrink-0">✨</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground mb-1">Request a Feature</h3>
                      <p className="text-sm text-muted-foreground">
                        Have an idea that would make Cascady better? We'd love to hear it.
                      </p>
                    </div>
                    {user ? (
                      <ChevronRight className="w-5 h-5 text-gray-400 mt-1 shrink-0" />
                    ) : (
                      <Badge variant="secondary" className="text-xs mt-1 shrink-0">Login required</Badge>
                    )}
                  </div>
                </div>

                {/* AI Help Chat */}
                <div
                  className={`border rounded-lg p-5 transition-all ${user ? 'hover:shadow-md cursor-pointer hover:border-primary-200' : 'opacity-70'}`}
                  onClick={user ? () => openFeedback('help') : undefined}
                  role={user ? 'button' : undefined}
                  tabIndex={user ? 0 : undefined}
                >
                  <div className="flex items-start gap-4">
                    <span className="text-2xl shrink-0">💬</span>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground mb-1">Ask for Help</h3>
                      <p className="text-sm text-muted-foreground">
                        Chat with our AI assistant for instant answers, or escalate to our support team.
                      </p>
                    </div>
                    {user ? (
                      <ChevronRight className="w-5 h-5 text-gray-400 mt-1 shrink-0" />
                    ) : (
                      <Badge variant="secondary" className="text-xs mt-1 shrink-0">Login required</Badge>
                    )}
                  </div>
                </div>

                {!user && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    <Link to="/auth" className="text-primary-600 underline hover:text-primary-700">Log in</Link> to access in-app support features.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Company Info */}
      <section className="py-16">
        <Container size="lg">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-foreground mb-6">Company Information</h2>
            <div className="bg-white border rounded-lg p-6 text-left">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p><strong className="text-foreground">Company:</strong> Zero Point Studio d.o.o.</p>
                <p><strong className="text-foreground">Address:</strong> Rudeška cesta 179, 10000 Zagreb, Croatia</p>
                <p><strong className="text-foreground">Director:</strong> Andrej Šimunaj</p>
                <p><strong className="text-foreground">Phone:</strong> +385 91 722 8780</p>
                <p><strong className="text-foreground">Email:</strong>{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary-600 underline">{SUPPORT_EMAIL}</a>
                </p>
                <p><strong className="text-foreground">Registration Date:</strong> April 24, 2024</p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <PublicFooter />

      {/* Feedback Modal */}
      {feedbackOpen && (
        <FeedbackModal onClose={() => setFeedbackOpen(false)} initialTab={feedbackTab} />
      )}
    </div>
  );
}
